import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useKeyboardShortcuts, SHORTCUTS_GLOBALES } from "./hooks/useKeyboardShortcuts";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard/Dashboard";
import DashboardEjecutivo from "./pages/DashboardEjecutivo/DashboardEjecutivo";
import FeedbackBetaPanel from "./pages/FeedbackBeta/FeedbackBetaPanel";
import ExpedientesHome from "./pages/Expedientes/ExpedientesHome";
import LoginForm from "./components/LoginForm";
import ForzarCambioPassword from "./components/ForzarCambioPassword";
import {
    CssBaseline,
    ThemeProvider,
    StyledEngineProvider,
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    Box,
    Chip,
    TextField,
    CircularProgress,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";
import makeStyles from '@mui/styles/makeStyles';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale/es';
import ExpedienteDetalle from "./pages/Expedientes/ExpedienteDetalle";
import HonorariosHome from "./pages/Honorarios/HonorariosHome";
import InactividadHome from "./pages/Inactividad/InactividadHome";
import BandejaSalidaHome from "./pages/BandejaSalida/BandejaSalidaHome";
import SolicitudDetalle from "./pages/Honorarios/SolicitudDetalle";
import SolicitudImpresion from "./pages/Honorarios/SolicitudImpresion";
import HonorariosAdminHome from "./pages/Honorarios/HonorariosAdminHome";
import AuditoriaHome from "./pages/Auditoria/AuditoriaHome";
import Documentos from "./pages/Documentos/Documentos";
import { PermisosProvider } from "./utils/usePermisos";

import { NotificationProvider } from "./contexts/NotificationContext";

import MisListasUnificadas from "./components/ListasTrabajo/GestionListas/MisListasUnificadas";
import Perfil from "./pages/Perfil/Perfil";
import ReadjudicacionMasiva from "./pages/ReadjudicacionMasiva/ReadjudicacionMasiva";
import LoteAuditoria from "./pages/ReadjudicacionMasiva/LoteAuditoria";
import LoteGestionAbogados from "./pages/ReadjudicacionMasiva/LoteGestionAbogados";
import LoteGenerarDocumentos from "./pages/ReadjudicacionMasiva/LoteGenerarDocumentos";
import LotePasaje from "./pages/ReadjudicacionMasiva/LotePasaje";
import CargaMasiva from "./pages/CargaMasiva/CargaMasiva";
import RecepcionBarras from "./pages/CargaMasiva/RecepcionBarras";
import LoteCargaMasivaDetalle from "./pages/CargaMasiva/LoteDetalle";
import PlanesPagoDGR from "./pages/ActualizacionDGR/PlanesPagoDGR";
import ConciliacionExterna from "./pages/ConciliacionExterna/ConciliacionExterna";
import PlantillasCargaMasiva from "./pages/CargaMasiva/PlantillasCargaMasiva";
import GestionLotes from "./pages/GestionLotes/GestionLotes";
import PanelFirmaAbogado from "./pages/PanelFirmaAbogado/PanelFirmaAbogado";
import PanelFirma from "./pages/PanelFirma/PanelFirma";
import BibliotecaHome from "./pages/Biblioteca/BibliotecaHome";
import CustodiaHome from "./pages/Custodia/CustodiaHome";
import PermisosHome from "./pages/Permisos/PermisosHome";
import PersonalHome from "./pages/Personal/PersonalHome";

import { loginJWT, userInfoJWT, permisosUsuarioJWT, logoutJWT, resetSessionExpired } from "./api/endpoints";
import { useAutoRefreshJWT } from "./hooks/useAutoRefreshJWT";

import "./styles/main.css";

const drawerWidth = 220;
const drawerClosedWidth = 56;

const useStyles = makeStyles(theme => ({
    main: props => ({
        flexGrow: 1,
        padding: 24,
        marginLeft: props.menuOpen ? drawerWidth : drawerClosedWidth,
        transition: "margin-left 0.3s cubic-bezier(.4,0,.2,1)",
        marginTop: 64,
        minHeight: `calc(100vh - 64px)`
    })
}));

function App() {
    const [usuario, setUsuario] = useState(null);
    const [moduloActivo, setModuloActivo] = useState(null);
    // Modo oscuro: infraestructura lista pero oculto hasta revisar componentes con colores hardcoded
    // eslint-disable-next-line no-unused-vars
    const [darkMode, setDarkMode] = useState(false);
    const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);
    const [permisosUsuario, setPermisosUsuario] = useState(null);
    const [permisosPorModulo, setPermisosPorModulo] = useState(null);

    // Estado para modal de sesión expirada
    const [sessionExpired, setSessionExpired] = useState(false);
    const [reloginUser, setReloginUser] = useState('');
    const [reloginPass, setReloginPass] = useState('');
    const [reloginError, setReloginError] = useState('');
    const [reloginLoading, setReloginLoading] = useState(false);

    // Escuchar evento global de sesión expirada.
    // Al abrir, pre-rellenamos `reloginUser` con el usuario que tiene la
    // sesión vigente. **Importante**: si el dialog YA está abierto, NO reseteamos
    // los inputs — el evento puede dispararse varias veces (cada poll en background
    // que recibe 401), y reiniciar `reloginPass` borra lo que el usuario estaba
    // escribiendo en ese momento.
    useEffect(() => {
        const handler = () => {
            if (sessionExpired) return; // Ya abierto: no pisar lo tipeado
            setSessionExpired(true);
            setReloginError('');
            setReloginPass('');
            if (usuario?.usuario && !reloginUser) {
                setReloginUser(usuario.usuario);
            }
        };
        window.addEventListener('sigif-session-expired', handler);
        return () => window.removeEventListener('sigif-session-expired', handler);
    }, [usuario, reloginUser, sessionExpired]);

    // Re-login desde el modal
    const handleRelogin = async (e) => {
        e.preventDefault();
        // Limpiamos espacios al borde — los password managers a veces los meten
        const userClean = (reloginUser || usuario?.usuario || '').trim();
        const passClean = (reloginPass || '');
        if (!userClean || !passClean) {
            setReloginError('Ingresá usuario y contraseña.');
            return;
        }
        setReloginError('');
        setReloginLoading(true);
        try {
            const data = await loginJWT({ usuario: userClean, password: passClean });
            if (data?.ok) {
                resetSessionExpired();
                setSessionExpired(false);
                setReloginPass('');
                setReloginError('');
                // Recargar la página para refrescar datos con nuevo token
                window.location.reload();
            } else {
                // Distinguir mensaje de rate limit (429) del clásico de credenciales (401).
                // El backend de rate-limit devuelve un error genérico, no un mensaje útil.
                const msg = (data?.mensaje || '').toString();
                if (/rate limit|too many/i.test(msg)) {
                    setReloginError('Demasiados intentos. Esperá 1 minuto y volvé a probar.');
                } else {
                    setReloginError(msg || 'Usuario o contraseña incorrectos.');
                }
            }
        } catch (err) {
            setReloginError(err?.message || 'Error al conectar con el servidor.');
        } finally {
            setReloginLoading(false);
        }
    };

    const handleReloginGoLogin = () => {
        setSessionExpired(false);
        localStorage.removeItem("SIGIF_JWT_TOKEN");
        window.location.href = "/login";
    };

    // Validar usuario autenticado al iniciar la app (usando JWT)
    useEffect(() => {
        const path = (window.location.pathname || "").toLowerCase();
        if (path.startsWith("/login")) { setLoading(false); return; }

        // Verificar si hay token JWT.
        // NO cambiamos la URL al fallar la auth — preservamos la ruta original
        // para que después del login (o re-login) el usuario vuelva ahí mismo,
        // no a /login. El render condicional con `!usuario` muestra el LoginForm
        // de igual modo, y el usuario no pierde su contexto.
        const token = localStorage.getItem("SIGIF_JWT_TOKEN");
        if (!token) {
            setUsuario(null);
            setLoading(false);
            return;
        }

        userInfoJWT()
            .then(data => {
                if (data && data.ok && data.usuario) {
                    setUsuario(data.usuario);
                } else {
                    setUsuario(null);
                }
                setLoading(false);
            })
            .catch(() => {
                setUsuario(null);
                setLoading(false);
            });
    }, []);


    // Cargar permisos del usuario logueado
    useEffect(() => {
        if (!usuario) {
            setPermisosUsuario(null);
            setPermisosPorModulo(null);
            return;
        }
        permisosUsuarioJWT()
            .then(data => {
                setPermisosUsuario(data.permisos || {});
                setPermisosPorModulo(data.permisosPorModulo || {});
            })
            .catch(err => {
                setPermisosUsuario({});
                setPermisosPorModulo({});
            });
    }, [usuario]);


    const theme = createTheme({
        palette: {
            mode: darkMode ? "dark" : "light",
            primary: { main: "#1769aa" },
            secondary: { main: "#d32f2f" },
            background: { default: darkMode ? "#171c24" : "#f5f9fc" }
        },
        shape: { borderRadius: 4 },
        typography: { fontFamily: "Roboto, 'Segoe UI', Arial, sans-serif" },
        components: {
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 4 },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'small',
                },
            },
            MuiFormControl: {
                defaultProps: {
                    variant: 'outlined',
                    size: 'small',
                },
            },
            MuiSelect: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiAutocomplete: {
                defaultProps: {
                    size: 'small',
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 4,
                    },
                },
            },
        },
    });

    const classes = useStyles({ menuOpen });

    const handleLogout = async () => {
        try {
            await logoutJWT();
        } catch (err) {
            console.error("Error en logout:", err);
        }

        localStorage.removeItem("SIGIF_JWT_TOKEN");
        window.location.href = "/login";
    };

    // Auto-refresh JWT: refresca token cada 45 min si hay actividad, logout después de 1h de inactividad
    useAutoRefreshJWT(handleLogout);


if (loading) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span>Cargando...</span>
            </div>
        );
    }

    if (!usuario) {
        return <LoginForm setUser={setUsuario} />;
    }

    // Si el admin reseteó la contraseña, el usuario entra con una temporal y
    // tiene que cambiarla antes de poder usar el sistema.
    if (usuario.passwordTemporal) {
        return (
            <ForzarCambioPassword
                usuario={usuario}
                onCambiado={() => setUsuario(u => ({ ...u, passwordTemporal: false }))}
                onLogout={handleLogout}
            />
        );
    }

    if (!permisosUsuario || !permisosPorModulo) {
        return (
            <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span>Cargando permisos...</span>
            </div>
        );
    }

    // Componente interno para poder usar useLocation (debe estar dentro del Router)
    const AppLayout = () => {
        const location = useLocation();
        const navigate = useNavigate();
        const isPrintView = location.pathname.includes("/imprimir");
        const isAdminView = location.pathname.startsWith("/admin");
        const isPersonalView = location.pathname.startsWith("/personal");
        const showChrome = !isPrintView && !isAdminView && !isPersonalView;

        // Shortcuts globales
        useKeyboardShortcuts([
            { key: '/', ctrlKey: true, handler: () => setShortcutsHelpOpen(true) },
            { key: 'h', altKey: true, handler: () => navigate('/') },
            { key: 'p', altKey: true, handler: () => navigate('/perfil') },
        ]);

        return (
            <>
                {showChrome && (
                    <Header
                        usuario={usuario}
                        darkMode={darkMode}
                        setDarkMode={setDarkMode}
                        moduloActivo={moduloActivo}
                        setModuloActivo={setModuloActivo}
                        onLogout={handleLogout}
                        onDrawerToggle={() => setMenuOpen(open => !open)}
                    />
                )}
                <div style={{ display: "flex" }}>
                    {showChrome && (
                        <Sidebar
                            usuario={usuario}
                            moduloActivo={moduloActivo}
                            setModuloActivo={setModuloActivo}
                            menuOpen={menuOpen}
                            setMenuOpen={setMenuOpen}
                        />
                    )}
                    <main className={showChrome ? classes.main : undefined}>
                                        <Routes>
                                            {/* ========== RUTAS EXISTENTES ========== */}
                                            <Route path="/" element={<Dashboard usuario={usuario} moduloActivo={moduloActivo} />} />
                                            <Route path="/dashboard-ejecutivo" element={<DashboardEjecutivo />} />
                                            <Route path="/feedback-beta" element={<FeedbackBetaPanel />} />
                                            <Route path="/expedientes" element={<ExpedientesHome usuario={usuario} moduloActivo={moduloActivo} />} />
                                            <Route path="/expediente/:tipo/:id" element={<ExpedienteDetalle usuario={usuario} />} />
                                            <Route path="/documentos" element={<Documentos usuario={usuario} permisos={permisosUsuario} />} />
                                            <Route path="/listas-trabajo/mis-listas" element={<MisListasUnificadas />} />
                                            <Route path="/perfil" element={<Perfil usuario={usuario} />} />
                                            <Route path="/honorarios" element={<HonorariosHome />} />
                                            <Route path="/mi-actividad" element={<InactividadHome usuario={usuario} />} />
                                            <Route path="/bandeja-salida" element={<BandejaSalidaHome />} />
                                            <Route path="/honorarios/solicitud/:id" element={<SolicitudDetalle />} />
                                            <Route path="/honorarios/solicitud/:id/imprimir" element={<SolicitudImpresion />} />
                                            <Route path="/honorarios/admin" element={<HonorariosAdminHome />} />
                                            <Route path="/auditoria" element={<AuditoriaHome />} />
                                            <Route path="/biblioteca" element={<BibliotecaHome />} />
                                            <Route path="/custodia" element={<CustodiaHome />} />
                                            <Route path="/permisos" element={<PermisosHome />} />
                                            <Route path="/admin" element={<PermisosHome />} />

                                            {/* ========== PERSONAL ========== */}
                                            <Route path="/personal/*" element={<PersonalHome usuario={usuario} />} />

                                            {/* ========== READJUDICACIÓN MASIVA ========== */}
                                            <Route path="/readjudicacion-masiva" element={<ReadjudicacionMasiva />} />
                                            <Route path="/readjudicacion-masiva/auditoria/:id" element={<LoteAuditoria />} />
                                            <Route path="/readjudicacion-masiva/abogados/:id" element={<LoteGestionAbogados />} />
                                            <Route path="/readjudicacion-masiva/pasaje/:id" element={<LotePasaje />} />
                                            <Route path="/readjudicacion-masiva/documentos/:id" element={<LoteGenerarDocumentos />} />

                                            {/* ========== GESTIÓN DE LOTES (Readjudicación + Carga Masiva) ========== */}
                                            <Route path="/gestion-lotes" element={<GestionLotes />} />

                                            {/* ========== CARGA MASIVA (rutas de detalle) ========== */}
                                            <Route path="/carga-masiva" element={<CargaMasiva />} />
                                            <Route path="/carga-masiva/plantillas" element={<PlantillasCargaMasiva />} />
                                            <Route path="/carga-masiva/recepcion-dgr" element={<RecepcionBarras />} />
                                            <Route path="/panel-firma-abogado" element={<PanelFirmaAbogado />} />
                                            <Route path="/carga-masiva/:id" element={<LoteCargaMasivaDetalle />} />

                                            {/* ========== ACTUALIZACIÓN DGR ========== */}
                                            <Route path="/actualizacion-dgr/planes-pago" element={<PlanesPagoDGR />} />

                                            {/* ========== CONCILIACIÓN EXTERNA ========== */}
                                            <Route path="/conciliacion-externa" element={<ConciliacionExterna />} />

                                            {/* ========== PANEL DE FIRMA ========== */}
                                            <Route path="/panel-firma" element={<PanelFirma />} />

                                            <Route path="*" element={<Navigate to="/" />} />
                                        </Routes>
                                    </main>
                                </div>
                            </>
                        );
                    };

    return (
        <>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <StylesThemeProvider theme={theme}>
                        <CssBaseline />
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                        <NotificationProvider>
                            <PermisosProvider permisos={permisosUsuario} permisosPorModulo={permisosPorModulo}>
                                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                                    <AppLayout />
                                </Router>
                            </PermisosProvider>
                        </NotificationProvider>
                    </LocalizationProvider>
                    </StylesThemeProvider>
                </ThemeProvider>
            </StyledEngineProvider>

            {/* Dialog: ayuda de shortcuts */}
            <Dialog open={shortcutsHelpOpen} onClose={() => setShortcutsHelpOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Atajos de teclado</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {SHORTCUTS_GLOBALES.map((s, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                                <Typography variant="body2">{s.desc}</Typography>
                                <Chip label={s.combo} size="small" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Modal de sesión expirada */}
            <Dialog
                open={sessionExpired}
                disableEscapeKeyDown
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                        }
                    }
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '4px',
                        maxWidth: 380,
                        width: '90vw',
                        overflow: 'visible',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #1769aa 0%, #2196f3 100%)',
                    p: 3,
                    textAlign: 'center',
                    borderRadius: '4px 4px 0 0',
                    position: 'relative',
                }}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        margin: '0 auto 12px',
                    }}>
                        <LockIcon sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                        Sesión expirada
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                        Ingresá tus credenciales para continuar
                    </Typography>
                </Box>
                <DialogContent sx={{ p: 3, pt: 2.5 }}>
                    <form onSubmit={handleRelogin} autoComplete="on">
                        <TextField
                            label="Usuario"
                            fullWidth
                            size="small"
                            value={reloginUser}
                            onChange={e => setReloginUser(e.target.value)}
                            sx={{ mb: 1.5 }}
                            inputProps={{ name: 'username', autoComplete: 'username' }}
                        />
                        <TextField
                            label="Contraseña"
                            type="password"
                            fullWidth
                            size="small"
                            value={reloginPass}
                            onChange={e => setReloginPass(e.target.value)}
                            sx={{ mb: 1.5 }}
                            inputProps={{ name: 'password', autoComplete: 'current-password' }}
                            autoFocus
                        />
                        {reloginError && (
                            <Typography variant="body2" color="error" sx={{ mb: 1, textAlign: 'center', fontSize: '0.8rem' }}>
                                {reloginError}
                            </Typography>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={reloginLoading || !reloginPass}
                            sx={{ py: 1, fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' }}
                        >
                            {reloginLoading ? <CircularProgress size={22} color="inherit" /> : 'Reiniciar sesión'}
                        </Button>
                    </form>
                </DialogContent>
                <Box sx={{ textAlign: 'center', pb: 2 }}>
                    <Button onClick={handleReloginGoLogin} color="inherit" size="small" sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Ir al login
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}

export default App;





